import * as ActionTypes from '../constants/ActionTypes';
import { find, first, last } from 'lodash';

import { logOut } from './auth';
import { setMessage } from './systemMessage';

import {
  resetPlan,
  saveHours,
  setPhotos,
  setInstagramPhotos,
  setDeleteImages,
  updateHours,
  updateTags,
  setLogo
} from './plan';

import { uploadImagesToCloudinary } from '../actions/cloudinary';
import { getVouchersByDeals } from './merchant';

import { 
  clearEntities, 
  receiveEntity, 
  receiveEntities 
} from '../actions/entities';

import Vendor from '../records/vendor';
import Hour from '../records/hour';
import Contact from '../records/contact';
import Image from '../records/image';
import User from '../records/user';
import SupplyNode from '../records/supply-node';
import SupplyInvite from '../records/supply-invite';
import Post from '../records/post';

import { CONFIG, Errors, Events } from 'ui-utilities';

export const customerLoaded = bool => ({
  type: ActionTypes.CUSTOMER_LOADED,
  payload: bool
});

export const customerResourceLoading = (resource, bool) => ({
  type: ActionTypes.CUSTOMER_RESOURCE_LOADING,
  resource: resource,
  value: bool
});

export const updateCustomerVendor = (vendor, reset = true) => {
  return (dispatch, getState) => {
    if (!vendor || (Array.isArray(vendor.data) && vendor.data.length < 1)) return false;

    // this will only be the case on a full page reload
    if (vendor.data && Array.isArray(vendor.data)) {
      vendor = vendor.data[0];
    } else if (vendor.data) {
      vendor = vendor.data;
    }

    dispatch({
      type: ActionTypes.UPDATE_CUSTOMER_VENDOR,
      payload: vendor.id
    });

    if (reset) dispatch(resetPlan());

    dispatch(clearEntities('supplyNodes'));
    dispatch(clearEntities('supplyInvites'));
    dispatch(clearEntities('following'));

    const v = new Vendor({
      id: vendor.id
    });

    // reload tags, followers, nodes, invites, posts and images for the new vendor
    return v.$getTags(vendor.id, updateTags)
      .then(res => v.$followers())
      .then(res => dispatch(receiveEntities(res.data, 'following')))
      .then(res => new Image({ owner_id: vendor.id }).$get())
      .then(res => new SupplyNode().$get(vendor.id))
      .then(res => new SupplyInvite().$get(vendor.id))
      .then(res => new Post().$get(vendor.id, 'vendor_id', 'special', true))
      .then(res => new Post().$get(vendor.id, 'vendor_id', 'deal', true))
      .then(res => dispatch(getVouchersByDeals()));
  }
};

export const getCustomerDetails = (reset = true) => {
  return (dispatch, getState) => {
    dispatch(customerLoaded(false));

    const activeVendorId = getState()
      .customer
      .get('account')
      .get('activeVendorId');

    const userId = getState()
      .auth
      .get('user')
      .get('user_id');

    return new User()
      .$get(userId)
      .then(res => { // res is an array of vendor api data and user data
        if (!activeVendorId) return Promise.all([dispatch(updateCustomerVendor(first(res), reset)), last(res)]);
        return Promise.resolve([last(res)]);
      })
      .then(res => dispatch({
        type: ActionTypes.GET_CUSTOMER_DETAILS,
        payload: last(res)
      }))
      .then(res => dispatch(customerLoaded(true)))
      .catch(err => {
        dispatch(customerLoaded(true));
        return new Errors(dispatch).async(err, logOut);
      });
  }
}

export const customerAdminTakeOverVendor = (vendorId, claimed, hidden, reset = false) => {
  return (dispatch, getState) => {
    dispatch(customerResourceLoading('vendors', true));

    if (reset) dispatch(clearEntities('vendors'));

    return new Vendor().$get(vendorId, 'id', claimed, hidden)
      .then(res => dispatch(updateCustomerVendor(res, true)))
      .then(res => dispatch(customerResourceLoading('vendors', false)));
  }
}

export const locateCustomer = (position) => {
  return dispatch => {
    localStorage.setItem('location', JSON.stringify(position));
    return dispatch({
      type: ActionTypes.LOCATE_CUSTOMER,
      payload: position
    });
  }
};

export const reverseGeolocate = position => {
  return (dispatch, getState) => {

    if (!position) {
      position = getState().customer.get('location').toJS();
    }

    fetch('https://api.mapbox.com/geocoding/v5/mapbox.places/' + position.coords.longitude + ',' + position.coords.latitude + '.json?access_token=' + CONFIG.MAPBOX_KEY, {
      headers: {
        'Content-Type': 'application/json'
      }
    }).then(res => res.json())
      .then(json => {
        if (json.features.length > 0) {
          let town = json.features[1];
          dispatch(locateCustomer({
            coords: {
              latitude: town.center[1],
              longitude: town.center[0]
            },
            place_name: town.place_name
          }));
        }
      }).catch(error => dispatch(setMessage('error', 'Unable to connect to geolocation service')));
  }
};

export const geoLocate = () => {
  return (dispatch, getState) => {
    if (navigator.geolocation) {
      return navigator.geolocation.getCurrentPosition(
        location => dispatch(reverseGeolocate(location)),
        error => dispatch(locateCustomer({ error: error }))
      );
    } else {
      return dispatch(locateCustomer({ error: 'No geolocation' }));
    }
  }
};

export const forwardGeolocate = place => {
  return (dispatch, getState) => {
    const location = place.line1 + ' ' + place.line2 + ' ' + place.locality + ' ' + place.region + ' ' + place.postal_code;
    const url = 'https://api.mapbox.com/geocoding/v5/mapbox.places/' + location + '.json?access_token=' + CONFIG.MAPBOX_KEY

    return fetch(url, {
      headers: {
        'Content-Type': 'application/json'
      }
    })
    .then(res => res.json())
    .then(json => {
      place = place.withMutations(map => map
        .set('longitude', json.features[0].geometry.coordinates[0])
        .set('latitude', json.features[0].geometry.coordinates[1])
      );
      return dispatch(receiveEntity(place, 'places'));
    });
  }
}

export const removeHours = () => {
  return (dispatch, getState) => {
    const { plan } = getState();

    if (!plan.get('hours').isSuperset(plan.get('editing_hours'))) {
      return Promise.all(plan.get('editing_hours')
        .map(h => h.map(j => {
          if (j.id && !j.open) {
            return j.$delete();
          } else {
            return Promise.resolve('No op');
          }
        })));
    } else {
      return Promise.resolve('No op');
    }
  }
}

export const resetCustomer = () => ({
  type: ActionTypes.RESET_CUSTOMER
});

export const removeImages = () => {
  return (dispatch, getState) => {
    const { customer, entities, plan } = getState();

    dispatch(customerResourceLoading('photos', true));

    return Promise.all(
      plan.get('delete_images')
        .map(i => entities
          .get('images')
          .get(i).$delete())
      )
      .then(res => dispatch(setPhotos([])))
      .then(res => dispatch(setDeleteImages([])))
      .then(res => dispatch(clearEntities('images')))
      .then(res => new Image().$get(customer.get('account').get('activeVendorId')))
      .then(res => dispatch(customerResourceLoading('photos', false)));
  }
}

export const updateImages = () => {
  return (dispatch, getState) => {
    const { customer } = getState();

    dispatch(customerResourceLoading('photos', true));

    return dispatch(uploadImagesToCloudinary())
      .then(images => Promise.all(images
        .map(i => new Image({ url: i }).$create(
          customer.get('account').get('activeVendorId')
        ))))
      .then(res => dispatch(setPhotos([])))
      .then(res => Events
        .provide('kissmetrics', true)
        .log(
          'record',
          'Profile.photos.upload'
        ))
      .then(res => dispatch(customerResourceLoading('photos', false)));
  }
}

export const saveProfile = inputs => {
  return (dispatch, getState) => {
    const { customer, entities, plan } = getState();
    let website, phone, vendor, place, twitter, facebook, instagram;

    const vendorId = customer
      .get('account')
      .get('activeVendorId');

    const logo = entities
      .get('images')
      .filter(i => i.get('owner_type') === 'vendors' && i.get('label') === 'logo');

    vendor = entities.get('vendors').get(vendorId).withMutations(map => map
      .set('name', find(inputs, i => i.label === 'business-name').validations.val)
      .set('cuisine', find(inputs, i => i.label === 'cuisine').validations.val)
      .set('price', '$'.repeat(find(inputs, i => i.label === 'price').validations.val))
      .set('description', find(inputs, i => i.label === 'description').validations.val)
      .set('short_description', find(inputs, i => i.label === 'short-description').validations.val)
    );

    place = vendor.places().first().withMutations(map => map
      .set('line1', find(inputs, i => i.label === 'business-address1').validations.val)
      .set('line2', find(inputs, i => i.label === 'business-address2').validations.val)
      .set('locality', find(inputs, i => i.label === 'business-city').validations.val)
      .set('region', find(inputs, i => i.label === 'business-state').validations.val)
    );

    place.contacts('phone').first() ?
      phone = place
        .contacts('phone')
        .first()
        .set('text', find(inputs, i => i.label === 'business-phone').validations.val) :
      phone = new Contact({
        text: find(inputs, i => i.label === 'business-phone').validations.val,
        owner_id: place.id,
        type: 'phone'
      });

    place.contacts('twitter').first() ?
      twitter = place
        .contacts('twitter').first()
        .set('text', find(inputs, i => i.label === 'contact-twitter').validations.val) :
      twitter = new Contact({
        text: find(inputs, i => i.label === 'contact-twitter').validations.val,
        owner_id: place.id,
        type: 'twitter'
      });

    place.contacts('instagram').first() ?
      instagram = place
        .contacts('instagram').first()
        .set('text', find(inputs, i => i.label === 'contact-instagram').validations.val) :
      instagram = new Contact({
        text: find(inputs, i => i.label === 'contact-instagram').validations.val,
        owner_id: place.id,
        type: 'instagram'
      });

    place.contacts('facebook').first() ?
      facebook = place
        .contacts('facebook').first()
        .set('text', find(inputs, i => i.label === 'contact-facebook').validations.val) :
      facebook = new Contact({
        text: find(inputs, i => i.label === 'contact-facebook').validations.val,
        owner_id: place.id,
        type: 'facebook'
      });

    place.contacts('website').first() ?
      website = place
        .contacts('website').first()
        .set('text', find(inputs, i => i.label === 'url').validations.val) :
      website = new Contact({
        text: find(inputs, i => i.label === 'url').validations.val,
        owner_id: place.id,
        type: 'website'
      });

    dispatch(customerResourceLoading('logo', true));
    dispatch(customerResourceLoading('profilePic', true));

    return Promise.all([
      vendor.$put(),
      dispatch(removeHours()),
      dispatch(saveHours(place.id, plan)),
      place.$put(),
      facebook.id  ? facebook.$put() : facebook.text !== '' && facebook.$create(),
      twitter.id ? twitter.$put() : twitter.text !== '' && twitter.$create(),
      instagram.id ? instagram.$put() : instagram.text !== '' && instagram.$create(),
      phone.id ? phone.$put() : phone.text !== '' && phone.$create(),
      website.id ? website.$put() : website.text !== '' && website.$create(),
      vendor.$putTags(null, updateTags),
      plan.get('logo') !== '' && Promise.all(logo.map(i => i.$delete())),
      plan.get('logo') !== '' && dispatch(uploadImagesToCloudinary(plan.get('logo')))
        .then(images => Promise.all(images.map(i => new Image({url: i, label: 'logo'}).$create(vendorId))))
        .then(res => Events
          .provide('kissmetrics', true)
          .log(
            'record',
            'Add Merchant Logo', {
              vendorId: vendorId
            }
          )),
    ])
    .then(res => dispatch(clearEntities('images')))
    .then(res => new Image().$get(vendor.id))
    .then(res => new Image().$get(place.id, 'places'))
    .then(res => new Hour().$get(place.id))
    .then(res => dispatch(setInstagramPhotos([])))
    .then(res => dispatch(setLogo("")))
    .then(res => dispatch(updateHours(plan.get('editing_hours'))))
    .then(res => {
      dispatch(customerResourceLoading('logo', false));
      return dispatch(customerResourceLoading('profilePic', false));
    })
    .then(res => dispatch(setMessage('success', 'Profile changes saved')))
    .then(res => Events
      .provide('kissmetrics', true)
      .log(
        'record',
        'Edit Profile', {
          vendorId: vendorId,
          'Business Description': vendor.description,
          'Merchant Name': vendor.name
        }
      ))
    .then(res => setTimeout(() => dispatch(setMessage('none', '')), 3000))
    .catch(err => new Errors(dispatch).async(err, logOut));
  }
}

export const toggleUserMenu = () => ({
  type: ActionTypes.TOGGLE_USER_MENU
});

export const adminUsersIndex = search => {
  return (dispatch, getState) => {
    return new User().$index(search);
  }
}



// WEBPACK FOOTER //
// ./src/actions/customer.js